'use client';

import { useTranslations } from 'next-intl';
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import Image from 'next/image';

interface InfoContentProps {
  locale: string;
}

export default function InfoContent({ locale }: InfoContentProps) {
  const t = useTranslations();
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const [activeSection, setActiveSection] = useState('about');
  const [contractContent, setContractContent] = useState<string>('');
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleContractSelect = useCallback(async (contractKey: string) => {
    try {
      // Türkçe dosya adı eşleştirmesi (kebab-case key'den dosya adına)
      const trFileMap: Record<string, string> = {
        'kvkk-aydinlatma-metni': 'kisisel-verilerin-korunmasi-aydinlatma-metni',
        'acik-artirma-genel-sartnamesi': 'acik-artirma-genel-sartnamesi',
        'alici-katilim-ve-teminat-taahhutnamesi': 'alici-katilim-ve-teminat-taahhutnamesi',
        'kazanan-teklif-satin-alma-protokolu': 'kazanan-teklif-satin-alma-protokolu',
        'uyelik-ve-kullanim': 'uyelik-ve-kullanim',
        'cerez-politikasi': 'cerez-politikasi',
        'tasinmaz-satis-talep-sozlesmesi': 'tasinmaz-satis-talep-sozlesmesi',
        'satici-yetkinlendirme-ve-aracilik-sozlesmesi': 'satici-yetkinlendirme-ve-aracilik-sozlesmesi',
        'acik-riza-metni': 'acik-riza-metni'
      };

      // İngilizce dosya adı eşleştirmesi (kebab-case key'den dosya adına)
      const enFileMap: Record<string, string> = {
        'kvkk-aydinlatma-metni': 'personal-data-protection-clarification',
        'acik-artirma-genel-sartnamesi': 'auction-general-terms-and-conditions',
        'alici-katilim-ve-teminat-taahhutnamesi': 'buyer-participation-and-security-undertaking',
        'kazanan-teklif-satin-alma-protokolu': 'winning-bid-purchase-protocol',
        'uyelik-ve-kullanim': 'membership-and-usage-terms',
        'cerez-politikasi': 'cookie-policy',
        'tasinmaz-satis-talep-sozlesmesi': 'real-estate-sale-request-agreement',
        'satici-yetkinlendirme-ve-aracilik-sozlesmesi': 'seller-authorization-and-intermediary-agreement',
        'acik-riza-metni': 'explicit-consent'
      };

      const fileKey = locale === 'tr' 
        ? trFileMap[contractKey] || contractKey
        : enFileMap[contractKey] || contractKey;
      
      const filePath = `/contracts/${fileKey}.html`;
      const response = await fetch(filePath);
      
      if (response.ok) {
        const htmlContent = await response.text();
        setContractContent(htmlContent);
      } else {
        if (process.env.NODE_ENV !== 'production') {
        console.error(`Sözleşme yüklenemedi: ${filePath} - Status: ${response.status}`);
        }
        setContractContent(locale === 'tr' 
          ? 'Sözleşme içeriği yüklenemedi.' 
          : 'Contract content could not be loaded.');
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
      console.error('Sözleşme yükleme hatası:', error);
      }
      setContractContent(locale === 'tr' 
        ? 'Sözleşme içeriği yüklenemedi.' 
        : 'Contract content could not be loaded.');
    }
  }, [locale]);

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const updateUrl = useCallback((sectionKey: string) => {
    const contractSections = ['acik-artirma-genel-sartnamesi', 'alici-katilim-ve-teminat-taahhutnamesi', 'kazanan-teklif-satin-alma-protokolu', 'kvkk-aydinlatma-metni', 'uyelik-ve-kullanim', 'cerez-politikasi', 'tasinmaz-satis-talep-sozlesmesi', 'satici-yetkinlendirme-ve-aracilik-sozlesmesi', 'acik-riza-metni'];
    const isContract = contractSections.includes(sectionKey);
    const params = new URLSearchParams();
    
    // İngilizce section key'leri Türkçe'ye map et
    const sectionMap: Record<string, string> = {
      'about': 'hakkimizda',
      'faq': 'sss',
      'contact': 'iletisim'
    };
    
    if (isContract) {
      params.set('sozlesme', sectionKey);
      params.delete('bolum');
      params.delete('contract'); // Eski parametreyi temizle
      params.delete('section'); // Eski parametreyi temizle
    } else {
      const turkceBolum = sectionMap[sectionKey] || sectionKey;
      params.set('bolum', turkceBolum);
      params.delete('sozlesme');
      params.delete('contract'); // Eski parametreyi temizle
      params.delete('section'); // Eski parametreyi temizle
    }
    
    const newUrl = `${pathname}?${params.toString()}`;
    router.replace(newUrl, { scroll: false });
  }, [pathname, router]);

  // URL parametresine göre aktif bölümü ayarla
  useEffect(() => {
    const bolum = searchParams.get('bolum');
    const sozlesme = searchParams.get('sozlesme');
    
    if (bolum && ['hakkimizda', 'sss', 'iletisim'].includes(bolum)) {
      // Eski İngilizce değerleri de destekle (geriye dönük uyumluluk)
      const sectionMap: Record<string, string> = {
        'hakkimizda': 'about',
        'sss': 'faq',
        'iletisim': 'contact'
      };
      setActiveSection(sectionMap[bolum] || bolum);
    }
    
    // Eğer sozlesme parametresi varsa, o sözleşmeyi seç
    if (sozlesme && ['acik-artirma-genel-sartnamesi', 'alici-katilim-ve-teminat-taahhutnamesi', 'kazanan-teklif-satin-alma-protokolu', 'kvkk-aydinlatma-metni', 'uyelik-ve-kullanim', 'cerez-politikasi', 'tasinmaz-satis-talep-sozlesmesi', 'satici-yetkinlendirme-ve-aracilik-sozlesmesi', 'acik-riza-metni'].includes(sozlesme)) {
      setActiveSection(sozlesme);
      handleContractSelect(sozlesme);
    }
    
    // Geriye dönük uyumluluk için eski parametreleri de kontrol et
    const section = searchParams.get('section');
    const contract = searchParams.get('contract');
    
    if (section && ['about', 'faq', 'contact'].includes(section)) {
      setActiveSection(section);
    }
    
    if (contract && ['acik-artirma-genel-sartnamesi', 'alici-katilim-ve-teminat-taahhutnamesi', 'kazanan-teklif-satin-alma-protokolu', 'kvkk-aydinlatma-metni', 'uyelik-ve-kullanim', 'cerez-politikasi', 'tasinmaz-satis-talep-sozlesmesi', 'satici-yetkinlendirme-ve-aracilik-sozlesmesi', 'acik-riza-metni'].includes(contract)) {
      setActiveSection(contract);
      handleContractSelect(contract);
    }
  }, [searchParams, handleContractSelect]);

  const sections = [
    { key: 'about', label: t('about.title') },
    { key: 'contact', label: t('contact.title') },
    { key: 'faq', label: t('about.faq.title') },
    { key: 'acik-artirma-genel-sartnamesi', label: locale === 'tr' ? 'Açık Artırma Genel Şartnamesi' : 'Open Auction General Terms' },
    { key: 'alici-katilim-ve-teminat-taahhutnamesi', label: locale === 'tr' ? 'Alıcı Katılım ve Teminat' : 'Buyer Participation' },
    { key: 'kazanan-teklif-satin-alma-protokolu', label: locale === 'tr' ? 'Kazanan Teklif Protokolü' : 'Winning Bid Protocol' },
    { key: 'kvkk-aydinlatma-metni', label: locale === 'tr' ? 'KVKK Aydınlatma Metni' : 'KVKK Information Text' },
    { key: 'uyelik-ve-kullanim', label: locale === 'tr' ? 'Üyelik ve Kullanım' : 'Membership Terms' },
    { key: 'cerez-politikasi', label: locale === 'tr' ? 'Çerez Politikası' : 'Cookie Policy' },
    { key: 'tasinmaz-satis-talep-sozlesmesi', label: locale === 'tr' ? 'Taşınmaz Satış Talep Sözleşmesi' : 'Real Estate Sale Request Contract' },
    { key: 'satici-yetkinlendirme-ve-aracilik-sozlesmesi', label: locale === 'tr' ? 'Satıcı Yetkinlendirme ve Aracılık Sözleşmesi' : 'Seller Authorization and Brokerage Contract' },
    { key: 'acik-riza-metni', label: locale === 'tr' ? 'Açık Rıza Metni' : 'Explicit Consent Text' }
  ];

  // Sözleşme olup olmadığını kontrol et
  const isContractSection = (section: string) => {
    const contractSections = ['acik-artirma-genel-sartnamesi', 'alici-katilim-ve-teminat-taahhutnamesi', 'kazanan-teklif-satin-alma-protokolu', 'kvkk-aydinlatma-metni', 'uyelik-ve-kullanim', 'cerez-politikasi', 'tasinmaz-satis-talep-sozlesmesi', 'satici-yetkinlendirme-ve-aracilik-sozlesmesi', 'acik-riza-metni'];
    return contractSections.includes(section);
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'about':
        return (
          <div className="space-y-12">
            {/* Company Introduction */}
            <div className="text-center mb-12">
              <div className="flex items-center justify-center mx-auto mb-6">
                <Image
                  src="/images/logo-key.png"
                  alt="Trink Emlak - Gayrimenkul Açık Artırma Platformu"
                  width={96}
                  height={96}
                  className="object-contain"
                  fetchPriority="high"
                />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-6">
                {t('about.intro.title')}
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed max-w-4xl mx-auto">
                {t('about.intro.description')}
              </p>
            </div>

            {/* Mission & Vision */}
            <div className="grid md:grid-cols-2 gap-8">
              {/* Mission */}
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-md">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('about.mission.title')}
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  {t('about.mission.description')}
                </p>
              </div>

              {/* Vision */}
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-md">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('about.vision.title')}
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  {t('about.vision.description')}
                </p>
              </div>
            </div>
          </div>
        );

      case 'faq':
        return (
          <div className="space-y-8">
            {/* FAQ Header */}
            <div className="text-center mb-12">
              <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                {t('about.faq.title')}
              </h1>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('about.faq.subtitle')}
              </p>
            </div>

            {/* FAQ Items */}
            <div className="max-w-4xl mx-auto">
              <div className="space-y-4">
                {t.raw('about.faq.questions').map((faq: { question: string; answer: string }, index: number) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200">
                    <button
                      onClick={() => toggleFaq(index)}
                      className="w-full px-6 py-4 text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 rounded-xl"
                    >
                      <span className="text-lg font-semibold text-gray-900 pr-4">
                        {faq.question}
                      </span>
                      <div className="flex-shrink-0">
                        <svg
                          className={`w-6 h-6 text-gray-500 transform transition-transform duration-200 ${
                            openFaqIndex === index ? 'rotate-180' : ''
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </button>
                    {openFaqIndex === index && (
                      <div className="px-6 pb-4">
                        <div className="border-t border-gray-100 pt-4">
                          <p className="text-gray-600 leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'contact':
        return (
          <div>
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                {t('contact.info.title')}
              </h2>
              
              <div className="space-y-8">
                {/* Address */}
                <div className="flex items-start space-x-4 group">
                  <div className="bg-gradient-to-br from-red-100 to-red-200 p-4 rounded-xl shadow-md group-hover:shadow-lg transition-all duration-300">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {t('contact.info.address')}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {t('contact.info.addressValue')}
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start space-x-4 group">
                  <div className="bg-gradient-to-br from-red-100 to-red-200 p-4 rounded-xl shadow-md group-hover:shadow-lg transition-all duration-300">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {t('contact.info.phone')}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {t('contact.info.phoneValue')}
                    </p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start space-x-4 group">
                  <div className="bg-gradient-to-br from-red-100 to-red-200 p-4 rounded-xl shadow-md group-hover:shadow-lg transition-all duration-300">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {t('contact.info.email')}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {t('contact.info.emailValue')}
                    </p>
                  </div>
                </div>

                {/* Working Hours */}
                <div className="flex items-start space-x-4 group">
                  <div className="bg-gradient-to-br from-red-100 to-red-200 p-4 rounded-xl shadow-md group-hover:shadow-lg transition-all duration-300">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {t('contact.info.workingHours')}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {t('contact.info.workingHoursValue')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="mt-12">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">
                  {t('contact.map.title')}
                </h3>
                <div className="rounded-xl overflow-hidden border border-gray-200 shadow-md">
                  <iframe
                    src={`https://www.google.com/maps?q=36.6194262,29.1326323&hl=${locale === 'tr' ? 'tr' : 'en'}&z=17&output=embed`}
                    width="100%"
                    height="400"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="w-full"
                    title={t('contact.map.title')}
                  />
                </div>
              </div>
            </div>
          </div>
        );


      // Sözleşme case'leri
      case 'acik-artirma-genel-sartnamesi':
      case 'alici-katilim-ve-teminat-taahhutnamesi':
      case 'kazanan-teklif-satin-alma-protokolu':
      case 'kvkk-aydinlatma-metni':
      case 'uyelik-ve-kullanim':
      case 'cerez-politikasi':
      case 'tasinmaz-satis-talep-sozlesmesi':
      case 'satici-yetkinlendirme-ve-aracilik-sozlesmesi':
      case 'acik-riza-metni':
        return (
          <div className="space-y-8">
            
            {contractContent ? (
              <div 
                className="contract-content-wrapper"
                dangerouslySetInnerHTML={{ __html: contractContent }}
              />
            ) : (
              <div className="text-center py-16">
                <svg className="w-20 h-20 mx-auto mb-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-gray-600 text-lg font-medium">
                  {locale === 'tr' 
                    ? 'Sözleşme içeriği yükleniyor...' 
                    : 'Loading contract content...'}
                </p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pt-16 lg:pt-20">
      <div className="container mx-auto px-4 py-4 lg:py-8">
        <div className="max-w-7xl mx-auto">

          {/* Mobil Menü Butonu */}
          <div className="lg:hidden mb-3">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="w-full bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3 flex items-center justify-between hover:shadow-md transition-shadow"
            >
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-3 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                <span className="font-semibold text-gray-900">
                  {sections.find(s => s.key === activeSection)?.label || t('info.selectSection')}
                </span>
              </div>
              <svg 
                className={`w-5 h-5 text-gray-400 transform transition-transform ${isMobileMenuOpen ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Mobil Menü Dropdown */}
            {isMobileMenuOpen && (
              <div className="mt-2 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                <nav className="p-3">
                  {/* Ana Bölümler */}
                  <div className="space-y-1 mb-3">
                    {sections.slice(0, 3).map((section) => (
                      <button
                        key={section.key}
                        onClick={() => {
                          setActiveSection(section.key);
                          updateUrl(section.key);
                          setIsMobileMenuOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2.5 rounded-lg font-medium transition-all duration-200 ${
                          activeSection === section.key
                            ? 'bg-red-50 text-red-600'
                            : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center">
                          {section.key === 'about' && (
                            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          )}
                          {section.key === 'contact' && (
                            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                          )}
                          {section.key === 'faq' && (
                            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          )}
                          <span>{section.label}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                  
                  {/* Ayırıcı */}
                  <div className="mb-3">
                    <div className="flex items-center">
                      <div className="flex-1 h-px bg-gray-200"></div>
                      <span className="px-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                        {locale === 'tr' ? 'Sözleşmeler' : 'Contracts'}
                      </span>
                      <div className="flex-1 h-px bg-gray-200"></div>
                    </div>
                  </div>
                  
                  {/* Sözleşme Linkleri */}
                  <div className="space-y-0.5">
                    {sections.slice(3).map((section) => (
                      <button
                        key={section.key}
                        onClick={() => {
                          setActiveSection(section.key);
                          updateUrl(section.key);
                          handleContractSelect(section.key);
                          setIsMobileMenuOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-all duration-200 ${
                          activeSection === section.key
                            ? 'bg-red-50 text-red-600'
                            : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="leading-snug">{section.label}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </nav>
              </div>
            )}
          </div>

          <div className={`grid lg:grid-cols-10 gap-8 ${isContractSection(activeSection) ? 'min-h-[calc(100vh-12rem)]' : ''}`}>
            {/* Sol Menü - Desktop */}
            <div className="hidden lg:block lg:col-span-3">
              <div className={`bg-white rounded-xl shadow-sm border border-gray-200 sticky top-24 ${isContractSection(activeSection) ? 'max-h-[calc(100vh-8rem)] overflow-y-auto' : ''}`}>
                <nav className="p-6">
                  {/* Ana Bölümler */}
                  <div className="space-y-1 mb-6">
                    {sections.slice(0, 3).map((section) => (
                      <button
                        key={section.key}
                        onClick={() => {
                          setActiveSection(section.key);
                          updateUrl(section.key);
                        }}
                        className={`w-full text-left px-4 py-3 rounded-lg font-medium text-lg transition-all duration-200 group ${
                          activeSection === section.key
                            ? 'text-red-600'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <div className="flex items-center">
                          {section.key === 'about' && (
                            <svg className={`w-5 h-5 mr-4 ${activeSection === section.key ? 'text-red-600' : 'text-gray-400 group-hover:text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          )}
                          {section.key === 'contact' && (
                            <svg className={`w-5 h-5 mr-4 ${activeSection === section.key ? 'text-red-600' : 'text-gray-400 group-hover:text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                          )}
                          {section.key === 'faq' && (
                            <svg className={`w-5 h-5 mr-4 ${activeSection === section.key ? 'text-red-600' : 'text-gray-400 group-hover:text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          )}
                          <span>{section.label}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                  
                  {/* Ayırıcı */}
                  <div className="mb-4">
                    <div className="flex items-center">
                      <div className="flex-1 h-px bg-gray-200"></div>
                      <span className="px-3 text-xs font-medium text-gray-500 uppercase tracking-wide">
                        {locale === 'tr' ? 'Sözleşmeler' : 'Contracts'}
                      </span>
                      <div className="flex-1 h-px bg-gray-200"></div>
                    </div>
                  </div>
                  
                  {/* Sözleşme Linkleri */}
                  <div className="space-y-0.5">
                    {sections.slice(3).map((section) => (
                      <button
                        key={section.key}
                        onClick={() => {
                          setActiveSection(section.key);
                          updateUrl(section.key);
                          handleContractSelect(section.key);
                        }}
                        className={`w-full text-left px-4 py-2.5 rounded-md text-base transition-all duration-200 group ${
                          activeSection === section.key
                            ? 'text-red-600'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <div className="flex items-center">
                          <svg className={`w-4 h-4 mr-3 ${activeSection === section.key ? 'text-red-600' : 'text-gray-400 group-hover:text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="leading-snug">{section.label}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </nav>
              </div>
            </div>

            {/* Sağ İçerik */}
            <div className="lg:col-span-7 w-full">
              <div className={`bg-white rounded-xl shadow-sm border border-gray-200 ${isContractSection(activeSection) ? 'max-h-[calc(100vh-8rem)] overflow-y-auto' : ''}`}>
                <div className="p-4 lg:p-8">
                  {renderContent()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
